import { useState } from 'react'
import { evalCond, EFFECT_ACTIONS, COST_TYPES, COST_TYPE_META } from './useRecipeState'
import EffectSentence from './EffectSentence'
import FormulaBuilder from './FormulaBuilder'

/* ─── Inline Effect Builder ─── */
const InlineEffectBuilder = ({ comps, inputSlots, laborRates, equipmentRates, onSave, onCancel }) => {
  const [step, setStep] = useState('action')
  const [action, setAction] = useState(null)
  const [targetIdx, setTargetIdx] = useState(null)
  const [multiplier, setMultiplier] = useState(1.5)
  const [note, setNote] = useState('')
  const [formula, setFormula] = useState('')

  // Add component fields
  const [addCostType, setAddCostType] = useState('labor')
  const [addDesc, setAddDesc] = useState('')
  const [addRole, setAddRole] = useState('')

  const handleSave = () => {
    const effect = { action, note }

    if (action === 'exclude_component' || action === 'include_component') {
      const comp = comps[targetIdx]
      effect.targetDescription = comp?.description || ''
      effect.targetIndex = targetIdx
    } else if (action === 'multiply_qty') {
      const comp = comps[targetIdx]
      effect.targetDescription = comp?.description || ''
      effect.targetIndex = targetIdx
      effect.value = multiplier
    } else if (action === 'add_component') {
      effect.value = {
        costType: addCostType,
        description: addDesc,
        laborRole: addCostType === 'labor' ? addRole : '',
        equipmentRole: addCostType === 'equipment' ? addRole : '',
        quantityMode: 'service_total',
        defaultQty: 0,
        isOptional: false,
        followsLeadHours: false
      }
    } else if (action === 'formula_qty') {
      const comp = comps[targetIdx]
      effect.targetDescription = comp?.description || ''
      effect.targetIndex = targetIdx
      effect.formula = formula
    }

    onSave(effect)
  }

  return (
    <div style={{ padding: 12, background: 'var(--bg-2)', borderRadius: 'var(--r)', border: '1px solid var(--line)', marginTop: 8 }}>
      {/* Step 1: Pick action */}
      {step === 'action' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Choose effect type
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {EFFECT_ACTIONS.map(ea => (
              <button
                key={ea.key}
                className="btn"
                style={{ fontSize: 11, padding: '6px 12px' }}
                onClick={() => {
                  setAction(ea.key)
                  if (ea.key === 'add_component') setStep('add_details')
                  else setStep('target')
                }}
              >
                <span>{ea.icon}</span> {ea.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Pick target component */}
      {step === 'target' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Select target component
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {comps.map((c, ci) => {
              const meta = COST_TYPE_META[c.costType] || COST_TYPE_META.labor
              return (
                <button
                  key={ci}
                  className="btn"
                  style={{ justifyContent: 'flex-start', fontSize: 12, padding: '6px 10px', textAlign: 'left' }}
                  onClick={() => {
                    setTargetIdx(ci)
                    if (action === 'multiply_qty') setStep('multiplier')
                    else if (action === 'formula_qty') setStep('formula')
                    else setStep('note')
                  }}
                >
                  <span className={`chip chip-sm ${meta.chipClass}`}>
                    {meta.label}
                  </span>
                  {c.description || `Component ${ci + 1}`}
                </button>
              )
            })}
          </div>
          <button className="btn" style={{ marginTop: 8, fontSize: 11 }} onClick={() => setStep('action')}>Back</button>
        </div>
      )}

      {/* Step 3a: Multiplier */}
      {step === 'multiplier' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Scale factor for {comps[targetIdx]?.description}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {[0.5, 0.75, 1.25, 1.5, 2.0, 3.0].map(m => (
              <button
                key={m}
                className={`btn${multiplier === m ? ' btn-primary' : ''}`}
                style={{ fontSize: 12, padding: '6px 12px', fontFamily: 'var(--font-mono)', fontWeight: 700 }}
                onClick={() => setMultiplier(m)}
              >
                {'\u00D7'}{m}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>Custom:</span>
            <input
              type="number"
              className="bare mono"
              value={multiplier}
              step="0.1"
              onChange={e => setMultiplier(parseFloat(e.target.value) || 1)}
              style={{ width: 80 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn" style={{ fontSize: 11 }} onClick={() => setStep('target')}>Back</button>
            <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => setStep('note')}>Next</button>
          </div>
        </div>
      )}

      {/* Step 3b: Formula */}
      {step === 'formula' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Formula for {comps[targetIdx]?.description}
          </div>
          <FormulaBuilder
            formula={formula}
            onChange={setFormula}
            inputSlots={inputSlots}
            slotValues={{}}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button className="btn" style={{ fontSize: 11 }} onClick={() => setStep('target')}>Back</button>
            <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => setStep('note')}>Next</button>
          </div>
        </div>
      )}

      {/* Step 3c: Add component details */}
      {step === 'add_details' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            New component details
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', marginBottom: 2 }}>Cost Type</div>
                <select className="bare" value={addCostType} onChange={e => setAddCostType(e.target.value)} style={{ width: 140 }}>
                  {COST_TYPES.map(ct => <option key={ct} value={ct}>{COST_TYPE_META[ct].label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', marginBottom: 2 }}>Description</div>
                <input className="bare" value={addDesc} onChange={e => setAddDesc(e.target.value)} placeholder="Component description" />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)', marginBottom: 2 }}>
                {addCostType === 'labor' ? 'Labor Role' : addCostType === 'equipment' ? 'Equipment' : 'Role/Item'}
              </div>
              <input
                className="bare"
                value={addRole}
                onChange={e => setAddRole(e.target.value)}
                placeholder={addCostType === 'labor' ? 'e.g. General Laborer' : 'e.g. Skid Steer'}
                list="add-role-list"
              />
              <datalist id="add-role-list">
                {addCostType === 'labor' && (laborRates || []).map(r => <option key={r._id} value={r.role} />)}
                {addCostType === 'equipment' && (equipmentRates || []).map(r => <option key={r._id} value={r.name} />)}
              </datalist>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button className="btn" style={{ fontSize: 11 }} onClick={() => setStep('action')}>Back</button>
            <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => setStep('note')}>Next</button>
          </div>
        </div>
      )}

      {/* Step 4: Note + save */}
      {step === 'note' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Add a note (optional)
          </div>
          <input
            className="bare"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Optional note about this effect"
            style={{ marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn" style={{ fontSize: 11 }} onClick={() => {
              if (action === 'multiply_qty') setStep('multiplier')
              else if (action === 'formula_qty') setStep('formula')
              else if (action === 'add_component') setStep('add_details')
              else setStep('target')
            }}>Back</button>
            <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={handleSave}>Add Effect</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <button className="btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

/* ─── Count total effects across all variations ─── */
function countAllEffects(vars) {
  let n = 0
  for (const v of vars) {
    for (const r of (v.rules || [])) {
      n += (r.effects || []).length
    }
  }
  return n
}

/* ═══════════════════════════════════════════════════════════
   VariationsTab
   ═══════════════════════════════════════════════════════════ */
const VariationsTab = ({ vars, setVars, comps, inputSlots, laborRates, equipmentRates, onConfirm }) => {
  const [buildingEffect, setBuildingEffect] = useState(null) // { variationIdx, optionValue }
  const [collapsed, setCollapsed] = useState({}) // { `${vi}-${oi}`: true }

  const toggleCollapsed = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))

  const update = (i, u) => setVars(vars.map((v, j) => j === i ? { ...v, ...u } : v))

  const remove = (i) => {
    const v = vars[i]
    onConfirm({
      title: 'Delete Variation',
      subtitle: 'This action cannot be undone',
      message: `Delete "${v.label || 'this variation'}" and all its rules/effects?`,
      actionLabel: 'Delete',
      onConfirm: () => setVars(vars.filter((_, j) => j !== i))
    })
  }

  const add = () => setVars([...vars, {
    attributeId: `var_${Date.now()}`,
    label: '',
    type: 'select',
    defaultValue: 'opt_1',
    options: [{ value: 'opt_1', label: 'Option 1' }, { value: 'opt_2', label: 'Option 2' }],
    rules: []
  }])

  const getEffectsForOption = (variation, optionValue) => {
    return (variation.rules || [])
      .filter(r => evalCond(r.when, optionValue))
      .flatMap(r => r.effects || [])
  }

  const handleEffectSave = (effect, variationIdx, optionValue) => {
    const newVars = JSON.parse(JSON.stringify(vars))
    const v = newVars[variationIdx]
    if (!v.rules) v.rules = []
    let rule = v.rules.find(r => r.when?.operator === 'eq' && r.when?.value === optionValue)
    if (!rule) {
      rule = { when: { operator: 'eq', value: optionValue }, effects: [] }
      v.rules.push(rule)
    }
    rule.effects.push(effect)
    setVars(newVars)
    setBuildingEffect(null)
  }

  const removeEffect = (variationIdx, optionValue, effectGlobalIdx) => {
    onConfirm({
      title: 'Remove Effect',
      subtitle: 'This action cannot be undone',
      message: `Remove this effect from the variation option?`,
      actionLabel: 'Remove',
      onConfirm: () => {
        const newVars = JSON.parse(JSON.stringify(vars))
        const nv = newVars[variationIdx]
        let count = 0
        for (const rule of (nv.rules || [])) {
          if (evalCond(rule.when, optionValue)) {
            for (let i = 0; i < (rule.effects || []).length; i++) {
              if (count === effectGlobalIdx) {
                rule.effects.splice(i, 1)
                if (!rule.effects.length) nv.rules = nv.rules.filter(r => r !== rule)
                setVars(newVars)
                return
              }
              count++
            }
          }
        }
      }
    })
  }

  const addOption = (varIdx) => {
    const newVars = [...vars]
    const v = { ...newVars[varIdx] }
    const opts = [...(v.options || [])]
    const val = `opt_${Date.now()}`
    opts.push({ value: val, label: `Option ${opts.length + 1}` })
    v.options = opts
    newVars[varIdx] = v
    setVars(newVars)
  }

  const removeOption = (varIdx, optIdx) => {
    const newVars = [...vars]
    const v = { ...newVars[varIdx] }
    v.options = (v.options || []).filter((_, i) => i !== optIdx)
    newVars[varIdx] = v
    setVars(newVars)
  }

  const updateOption = (varIdx, optIdx, field, val) => {
    const newVars = [...vars]
    const v = { ...newVars[varIdx] }
    const opts = [...(v.options || [])]
    opts[optIdx] = { ...opts[optIdx], [field]: val }
    v.options = opts
    newVars[varIdx] = v
    setVars(newVars)
  }

  const totalEffects = countAllEffects(vars)

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Variations</h2>
          {totalEffects > 0 && (
            <span className="chip chip-info chip-sm">{totalEffects} effects</span>
          )}
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={add}>+ Add variation</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 18px' }}>
        How recipes adapt to site reality. Read each rule as a sentence &mdash; click any chip to change it.
      </p>

      {vars.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">{'\uD83C\uDF2C\uFE0F'}</div>
          <div className="empty-title">No variations defined</div>
          <div className="empty-subtitle">Add variations like "Soil Condition" or "Access Type" to adjust recipe costs</div>
        </div>
      )}

      {/* Variation cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {vars.map((v, vi) => {
          const typeIcon = v.type === 'boolean' ? '\u26A1' : '\u2630'
          return (
            <div key={vi} className="var-card">
              {/* Card header */}
              <div className="var-card-header">
                <span className="drag-handle" title="Drag to reorder">{'\u2630'}</span>
                <div style={{ flex: 1 }}>
                  <input
                    className="bare"
                    value={v.label || ''}
                    placeholder="Variation label..."
                    onChange={e => update(vi, { label: e.target.value })}
                    style={{ fontSize: 16, fontWeight: 600, width: '100%' }}
                  />
                </div>
                <span className="var-key-badge">{v.attributeId}</span>
                <select
                  className="bare var-type-select"
                  value={v.type}
                  onChange={e => update(vi, { type: e.target.value })}
                >
                  <option value="select">Select</option>
                  <option value="boolean">Boolean</option>
                  <option value="range">Range</option>
                </select>
                <button className="row-delete" onClick={() => remove(vi)}>{'\u00D7'}</button>
              </div>

              {/* Select type: Options list */}
              {v.type === 'select' && (
                <div className="var-options-list">
                  {(v.options || []).map((opt, oi) => {
                    const isDefault = opt.value === v.defaultValue
                    const effects = getEffectsForOption(v, opt.value)
                    const isBuilding = buildingEffect?.variationIdx === vi && buildingEffect?.optionValue === opt.value
                    const collapseKey = `${vi}-${oi}`
                    const isCollapsed = collapsed[collapseKey] && !isDefault

                    return (
                      <div key={oi} className={`opt${isDefault ? ' selected' : ''}`}>
                        <button
                          className="opt-radio"
                          onClick={() => update(vi, { defaultValue: opt.value })}
                        >
                          {isDefault && <span className="opt-radio-fill" />}
                        </button>
                        <div className="opt-body">
                          <div className="opt-row">
                            <input
                              className="bare opt-label"
                              value={opt.label || ''}
                              placeholder="Option label"
                              onChange={e => updateOption(vi, oi, 'label', e.target.value)}
                            />
                            {isDefault && <span className="chip chip-primary chip-sm">Default</span>}
                            <button
                              className="opt-close"
                              onClick={() => removeOption(vi, oi)}
                            >{'\u00D7'}</button>
                          </div>

                          {/* Effects for this option */}
                          {effects.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {effects.map((eff, ei) => (
                                <EffectSentence
                                  key={ei}
                                  effect={eff}
                                  onRemove={() => removeEffect(vi, opt.value, ei)}
                                />
                              ))}
                            </div>
                          )}

                          {isDefault && effects.length === 0 && (
                            <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic', padding: '4px 0' }}>
                              No changes &mdash; this is the baseline recipe.
                            </div>
                          )}

                          {/* Add effect — ghost text link */}
                          {isBuilding ? (
                            <InlineEffectBuilder
                              comps={comps}
                              inputSlots={inputSlots}
                              laborRates={laborRates}
                              equipmentRates={equipmentRates}
                              onSave={(eff) => handleEffectSave(eff, vi, opt.value)}
                              onCancel={() => setBuildingEffect(null)}
                            />
                          ) : (
                            <button
                              className="btn-link"
                              style={{ marginTop: 8 }}
                              onClick={() => setBuildingEffect({ variationIdx: vi, optionValue: opt.value })}
                            >+ Add effect</button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  <div className="v-foot">
                    <button
                      className="btn btn-outlined btn-sm"
                      onClick={() => addOption(vi)}
                    >+ Add option</button>
                  </div>
                </div>
              )}

              {/* Boolean type */}
              {v.type === 'boolean' && (
                <div className="var-options-list">
                  {[false, true].map(boolVal => {
                    const label = boolVal ? 'When ENABLED' : 'When DISABLED'
                    const isDefault = v.defaultValue === boolVal
                    const effects = getEffectsForOption(v, boolVal)
                    const isBuilding = buildingEffect?.variationIdx === vi && buildingEffect?.optionValue === boolVal

                    return (
                      <div key={String(boolVal)} className={`opt${isDefault ? ' selected' : ''}`}>
                        <div className="opt-body" style={{ width: '100%' }}>
                          <div className="opt-row">
                            <span style={{ fontSize: 14, fontWeight: 600, color: boolVal ? 'var(--primary-2)' : 'var(--ink-3)' }}>
                              {label}
                            </span>
                            {isDefault && <span className="chip chip-primary chip-sm">Default</span>}
                          </div>

                          {isDefault && effects.length === 0 && (
                            <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic', padding: '4px 0' }}>
                              No changes &mdash; this is the baseline recipe.
                            </div>
                          )}

                          {effects.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {effects.map((eff, ei) => (
                                <EffectSentence key={ei} effect={eff} onRemove={() => removeEffect(vi, boolVal, ei)} />
                              ))}
                            </div>
                          )}

                          {isBuilding ? (
                            <InlineEffectBuilder
                              comps={comps}
                              inputSlots={inputSlots}
                              laborRates={laborRates}
                              equipmentRates={equipmentRates}
                              onSave={(eff) => handleEffectSave(eff, vi, boolVal)}
                              onCancel={() => setBuildingEffect(null)}
                            />
                          ) : (
                            <button
                              className="btn-link"
                              style={{ marginTop: 8 }}
                              onClick={() => setBuildingEffect({ variationIdx: vi, optionValue: boolVal })}
                            >+ Add effect</button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Range type */}
              {v.type === 'range' && (
                <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)' }}>Min</span>
                    <input type="number" className="bare mono" value={v.min ?? 0} onChange={e => update(vi, { min: parseFloat(e.target.value) || 0 })} style={{ width: 60, textAlign: 'center' }} />
                  </div>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)' }}>Max</span>
                    <input type="number" className="bare mono" value={v.max ?? 100} onChange={e => update(vi, { max: parseFloat(e.target.value) || 100 })} style={{ width: 60, textAlign: 'center' }} />
                  </div>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)' }}>Step</span>
                    <input type="number" className="bare mono" value={v.step ?? 1} onChange={e => update(vi, { step: parseFloat(e.target.value) || 1 })} style={{ width: 60, textAlign: 'center' }} />
                  </div>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)' }}>Unit</span>
                    <input className="bare" value={v.unit || ''} onChange={e => update(vi, { unit: e.target.value })} placeholder="e.g. %" style={{ width: 60 }} />
                  </div>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4)' }}>Default</span>
                    <input type="number" className="bare mono" value={v.defaultValue ?? v.min ?? 0} onChange={e => update(vi, { defaultValue: parseFloat(e.target.value) || 0 })} style={{ width: 60, textAlign: 'center' }} />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default VariationsTab
